import { useState, useEffect, useContext } from "react"
import Header from "../../../layouts/Header"
import BackIcon from "../../../components/BackIcon"
import { useNavigate, useLocation } from "react-router-dom"
import {
  Box,
  Paper,
  TextField,
  Tooltip,
  IconButton,
  Grid,
  Typography,
  MenuItem,
  Button,
  Card,
  CardContent,
  Alert,
} from "@mui/material"
import { createTheme, ThemeProvider } from "@mui/material/styles"
import { Add, Delete, Edit as EditIcon, Lock } from "@mui/icons-material"
import CustomBackdrop from "../../../components/CustomBackdrop"
import { useMutation, useQuery, api, showWarning } from "../../../api"
import { GlobalContext } from "../../../contexts/GlobalContext"
import getIconComponent from "../../utils/getIconComponent"

const theme = createTheme({
  palette: { primary: { main: "#196C87" }, secondary: { main: "#2E7D32" } },
})

const StyledRoot = {
  width: "100%",
  maxWidth: "1200px",
  margin: "0 auto",
  padding: "20px",
  backgroundColor: "#f5f7fa",
  borderRadius: "12px",
  boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
}

const EditarEncuesta = () => {
  const navigate = useNavigate()
  const { state } = useLocation()
  const { selectedMenuInfo } = useContext(GlobalContext)

  // Rescatamos el código de la encuesta seleccionada en la grilla
  const enccodigo = state?.encuesta?.enccodigo

  // ESTADO MAESTRO DE LA ENCUESTA
  const [formData, setFormData] = useState({
    enccodigo: "",
    enctitulo: "",
    encdescri: "",
    enctipo: "RAP",
    zoncodigo: "",
    encpublica: 0,
    encstatus: "A",
    preguntas: [],
  })

  // Bloqueo de seguridad
  const [isReadOnly, setIsReadOnly] = useState(false)

  // ⭐ 1. QUERY: TRAER TODA LA ENCUESTA (Defensivo y siempre fresco)
  // Agregamos 'refetch' para poder dispararlo manualmente
  const {
    data: encuestaData,
    isLoading: isLoadingData,
    refetch,
  } = useQuery({
    queryKey: ["getEncuestaCompleta", enccodigo],
    queryFn: async () => {
      if (!enccodigo) return null
      const res = await api.post("/Encuestas/getEncuestaCompleta", { enccodigo })

      // Desempaquetado seguro
      const payload = res?.success !== undefined ? res : res?.data
      if (payload && payload.success) {
        return payload.data
      }
      return null
    },
    enabled: !!enccodigo,
    refetchOnWindowFocus: false,
    refetchOnMount: "always", // ⭐ Obliga a recargar si la pantalla se vuelve a montar
  })

  // ⭐ NUEVO: Dispara una consulta obligatoria al servidor cada vez que abrimos la pantalla
  useEffect(() => {
    if (enccodigo) {
      refetch()
    }
  }, [enccodigo, refetch])

  // ⭐ 2. SINCRONIZACIÓN INFALIBLE AL FORMULARIO
  useEffect(() => {
    if (encuestaData && encuestaData.cabecera) {
      const cab = encuestaData.cabecera
      const pregs = encuestaData.preguntas || []
      const opcs = encuestaData.opciones || []

      // Reconstruimos la estructura padre-hijo-nieto para el Frontend
      const preguntasFormateadas = pregs.map((p) => ({
        id: p.pregsecuen, // Usamos la secuencia como ID único temporal para React
        pregtipo: p.pregtipo,
        pregtexto: p.pregtexto,
        pregponderacion: p.pregponderacion,
        pregobligatoria: p.pregobligatoria,
        pregstatus: p.pregstatus,
        opciones: opcs
          .filter((o) => o.pregsecuen === p.pregsecuen)
          .map((o) => ({
            id: o.opcsecuen,
            opctexto: o.opctexto,
            opcpuntaje: o.opcpuntaje,
            opcstatus: o.opcstatus,
          })),
      }))

      setFormData({
        enccodigo: cab.enccodigo,
        enctitulo: cab.enctitulo,
        encdescri: cab.encdescri || "",
        enctipo: cab.enctipo,
        zoncodigo: cab.zoncodigo || "",
        encpublica: cab.encpublica,
        encstatus: cab.encstatus,
        preguntas: preguntasFormateadas,
      })

      // LÓGICA DE BLOQUEO: Verificamos si tiene respuestas
      if (cab.tieneRespuestas && cab.tieneRespuestas > 0) {
        setIsReadOnly(true)
      }
    }
  }, [encuestaData])

  // 3. MUTATION: GUARDAR EDICIÓN
  const { mutateAsync: SaveEdicionEncuesta, isPending: isSaving } = useMutation({
    queryKey: ["isEditingEncuesta"],
    fn: async (payload) => {
      return await api.post("/Encuestas/saveEncuesta", payload)
    },
    showError: "modal",
    showSuccess: "toast",
    onSuccess: () => navigate(-1),
  })

  // ================= LÓGICA DE LA CABECERA =================
  const handleInputChange = (field, value) => {
    if (isReadOnly) return
    setFormData((prev) => ({ ...prev, [field]: value }))
  }

  // ================= LÓGICA DE PREGUNTAS =================
  const handleAddPregunta = () => {
    if (isReadOnly) return
    setFormData((prev) => ({
      ...prev,
      preguntas: [
        ...prev.preguntas,
        {
          id: Date.now(),
          pregtipo: "U",
          pregtexto: "",
          pregponderacion: 1.0,
          pregobligatoria: 1,
          pregstatus: "A",
          opciones: [],
        },
      ],
    }))
  }

  const handleRemovePregunta = (idToRemove) => {
    if (isReadOnly) return
    setFormData((prev) => ({
      ...prev,
      preguntas: prev.preguntas.filter((p) => p.id !== idToRemove),
    }))
  }

  const handleChangePregunta = (id, field, value) => {
    if (isReadOnly) return
    setFormData((prev) => ({
      ...prev,
      preguntas: prev.preguntas.map((p) => {
        if (p.id === id) {
          const updatedPregunta = { ...p, [field]: value }
          if (field === "pregtipo" && value === "U") updatedPregunta.opciones = []
          return updatedPregunta
        }
        return p
      }),
    }))
  }

  // ================= LÓGICA DE OPCIONES =================
  const handleAddOpcion = (preguntaId) => {
    if (isReadOnly) return
    setFormData((prev) => ({
      ...prev,
      preguntas: prev.preguntas.map((p) => {
        if (p.id === preguntaId) {
          return {
            ...p,
            opciones: [...p.opciones, { id: Date.now(), opctexto: "", opcpuntaje: 0.0, opcstatus: "A" }],
          }
        }
        return p
      }),
    }))
  }

  const handleRemoveOpcion = (preguntaId, opcionIdToRemove) => {
    if (isReadOnly) return
    setFormData((prev) => ({
      ...prev,
      preguntas: prev.preguntas.map((p) => {
        if (p.id === preguntaId) {
          return { ...p, opciones: p.opciones.filter((o) => o.id !== opcionIdToRemove) }
        }
        return p
      }),
    }))
  }

  const handleChangeOpcion = (preguntaId, opcionId, field, value) => {
    if (isReadOnly) return
    setFormData((prev) => ({
      ...prev,
      preguntas: prev.preguntas.map((p) => {
        if (p.id === preguntaId) {
          return {
            ...p,
            opciones: p.opciones.map((o) => (o.id === opcionId ? { ...o, [field]: value } : o)),
          }
        }
        return p
      }),
    }))
  }

  // ================= VALIDACIÓN Y GUARDADO =================
  const handleSubmit = async (e) => {
    if (e) e.preventDefault()
    if (isReadOnly) return showWarning("La encuesta está bloqueada y no puede ser editada.")

    if (!formData.enctitulo.trim()) return showWarning("El título de la encuesta es obligatorio.")
    if (formData.preguntas.length === 0) return showWarning("Debe agregar al menos una pregunta a la encuesta.")

    for (let i = 0; i < formData.preguntas.length; i++) {
      const p = formData.preguntas[i]
      if (!p.pregtexto.trim()) return showWarning(`La pregunta #${i + 1} no tiene texto.`)

      if (p.pregtipo === "L" || p.pregtipo === "M") {
        if (p.opciones.length === 0) return showWarning(`La pregunta #${i + 1} requiere al menos una opción.`)
        for (let j = 0; j < p.opciones.length; j++) {
          if (!p.opciones[j].opctexto.trim()) return showWarning(`La pregunta #${i + 1} tiene una opción sin texto.`)
        }
      }
    }

    await SaveEdicionEncuesta(formData)
  }

  // ================= BARRA DE ACCIONES =================
  const ejecutarAction = selectedMenuInfo?.data?.barraAcciones?.find(
    (action) => action?.acccaption === "GRABAR" || action?.acccaption === "EJECUTAR",
  )
  const toolbarActions = ejecutarAction
    ? [
        {
          label: ejecutarAction.acccaption,
          key: ejecutarAction.acccaption,
          icon: getIconComponent(ejecutarAction.accnameicono, ejecutarAction.acctipoico),
        },
      ]
    : []

  return (
    <ThemeProvider theme={theme}>
      <Header />
      <div className="main main-app p-3 p-lg-4">
        <BackIcon />

        {/* TOOLBAR */}
        <Box sx={{ mb: 2 }}>
          {toolbarActions.map((action) => (
            <Tooltip title={isReadOnly ? "Bloqueado (Ya existen respuestas)" : action.label} key={action.key}>
              <span>
                <IconButton
                  onClick={handleSubmit}
                  disabled={isSaving || isLoadingData || isReadOnly}
                  sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1, bgcolor: "white" }}
                >
                  {isReadOnly ? <Lock color="error" /> : action.icon}
                </IconButton>
              </span>
            </Tooltip>
          ))}
        </Box>

        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            margin: "0 30px 20px 30px",
            fontSize: "25px",
            color: "#196C87",
          }}
        >
          <EditIcon sx={{ fontSize: 32, mr: 1 }} />
          <b>Editar Encuesta Dinámica: {enccodigo || ""}</b>
        </div>

        {isReadOnly && (
          <Alert severity="error" sx={{ mb: 3, maxWidth: "1200px", mx: "auto" }}>
            <Typography variant="subtitle2">
              <b>¡Atención!</b> Esta encuesta ya tiene respuestas asociadas. Por integridad de los datos, el modo de
              edición ha sido bloqueado.
            </Typography>
          </Alert>
        )}

        <CustomBackdrop isLoading={isSaving || isLoadingData} />

        <Box sx={StyledRoot} component="form" onSubmit={handleSubmit}>
          {/* BLOQUE 1: CABECERA */}
          <Paper
            elevation={2}
            sx={{
              p: 4,
              mb: 4,
              borderRadius: 3,
              borderTop: "5px solid #196C87",
              background: "white",
              opacity: isReadOnly ? 0.8 : 1,
            }}
          >
            <Typography variant="h6" color="primary" fontWeight="bold" gutterBottom>
              Datos Generales de la Encuesta
            </Typography>
            <Grid container spacing={3} mt={1}>
              <Grid item xs={12} sm={8}>
                <TextField
                  fullWidth
                  label="Título de la Encuesta *"
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  value={formData.enctitulo}
                  onChange={(e) => handleInputChange("enctitulo", e.target.value)}
                  inputProps={{ maxLength: 200 }}
                  disabled={isReadOnly}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  select
                  fullWidth
                  label="Tipo de Encuesta *"
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  value={formData.enctipo}
                  onChange={(e) => handleInputChange("enctipo", e.target.value)}
                  disabled={isReadOnly}
                >
                  <MenuItem value="ENL">En línea</MenuItem>
                  <MenuItem value="TEL">Telefónicas</MenuItem>
                  <MenuItem value="PRE">Presenciales</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Descripción u Objetivos (Opcional)"
                  size="small"
                  multiline
                  rows={2}
                  InputLabelProps={{ shrink: true }}
                  value={formData.encdescri}
                  onChange={(e) => handleInputChange("encdescri", e.target.value)}
                  inputProps={{ maxLength: 1000 }}
                  disabled={isReadOnly}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  select
                  fullWidth
                  label="Audiencia Objetivo *"
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  value={formData.encpublica}
                  onChange={(e) => handleInputChange("encpublica", e.target.value)}
                  disabled={isReadOnly}
                >
                  <MenuItem value={0}>Lista Específica (Clientes/Usuarios)</MenuItem>
                  <MenuItem value={1}>Pública (Cualquier persona)</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Zona de Aplicación (Código)"
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  placeholder="Ej: Z01"
                  value={formData.zoncodigo}
                  onChange={(e) => handleInputChange("zoncodigo", e.target.value.toUpperCase())}
                  inputProps={{ maxLength: 3 }}
                  helperText="Deje en blanco si aplica a nivel nacional"
                  disabled={isReadOnly}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  select
                  fullWidth
                  label="Estado"
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  value={formData.encstatus}
                  onChange={(e) => handleInputChange("encstatus", e.target.value)}
                  disabled={isReadOnly}
                >
                  <MenuItem value="A">ACTIVA</MenuItem>
                  <MenuItem value="I">INACTIVA</MenuItem>
                </TextField>
              </Grid>
            </Grid>
          </Paper>

          {/* BLOQUE 2: CONSTRUCTOR DE PREGUNTAS */}
          <Typography
            variant="h6"
            color="primary"
            fontWeight="bold"
            gutterBottom
            sx={{ display: "flex", alignItems: "center", gap: 1 }}
          >
            Constructor de Preguntas
          </Typography>

          {formData.preguntas.map((preg, index) => (
            <Card
              key={preg.id}
              elevation={1}
              sx={{ mb: 3, borderLeft: "4px solid #f57c00", opacity: isReadOnly ? 0.8 : 1 }}
            >
              <CardContent sx={{ p: 3 }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography variant="subtitle1" fontWeight="bold" color="textSecondary">
                    Pregunta #{index + 1}
                  </Typography>
                  {!isReadOnly && (
                    <IconButton color="error" size="small" onClick={() => handleRemovePregunta(preg.id)}>
                      <Delete />
                    </IconButton>
                  )}
                </Box>

                <Grid container spacing={2}>
                  <Grid item xs={12} sm={8}>
                    <TextField
                      fullWidth
                      label="Texto de la Pregunta *"
                      size="small"
                      InputLabelProps={{ shrink: true }}
                      value={preg.pregtexto}
                      onChange={(e) => handleChangePregunta(preg.id, "pregtexto", e.target.value)}
                      disabled={isReadOnly}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      select
                      fullWidth
                      label="Tipo de Respuesta *"
                      size="small"
                      InputLabelProps={{ shrink: true }}
                      value={preg.pregtipo}
                      onChange={(e) => handleChangePregunta(preg.id, "pregtipo", e.target.value)}
                      disabled={isReadOnly}
                    >
                      <MenuItem value="U">Texto Libre (Abierta)</MenuItem>
                      <MenuItem value="L">Lista Desplegable (Cerrada)</MenuItem>
                      <MenuItem value="M">Casillas Múltiples</MenuItem>
                    </TextField>
                  </Grid>

                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      label="Ponderación (Peso) *"
                      size="small"
                      type="number"
                      InputLabelProps={{ shrink: true }}
                      value={preg.pregponderacion}
                      onChange={(e) =>
                        handleChangePregunta(preg.id, "pregponderacion", parseFloat(e.target.value) || 0)
                      }
                      inputProps={{ step: "0.1", min: "0" }}
                      disabled={isReadOnly}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      select
                      fullWidth
                      label="Obligatoria *"
                      size="small"
                      InputLabelProps={{ shrink: true }}
                      value={preg.pregobligatoria}
                      onChange={(e) => handleChangePregunta(preg.id, "pregobligatoria", e.target.value)}
                      disabled={isReadOnly}
                    >
                      <MenuItem value={1}>SI</MenuItem>
                      <MenuItem value={0}>NO</MenuItem>
                    </TextField>
                  </Grid>
                </Grid>

                {/* SUB-SECCIÓN: OPCIONES */}
                {(preg.pregtipo === "L" || preg.pregtipo === "M") && (
                  <Box sx={{ mt: 3, p: 2, bgcolor: "#fdfdfd", border: "1px dashed #ccc", borderRadius: 2 }}>
                    <Typography variant="caption" fontWeight="bold" color="primary" gutterBottom display="block">
                      Opciones de Respuesta
                    </Typography>

                    {preg.opciones.map((opc, opcIndex) => (
                      <Grid container spacing={2} alignItems="center" key={opc.id} sx={{ mb: 1 }}>
                        <Grid item xs={12} sm={7}>
                          <TextField
                            fullWidth
                            placeholder={`Opción ${opcIndex + 1}`}
                            size="small"
                            value={opc.opctexto}
                            onChange={(e) => handleChangeOpcion(preg.id, opc.id, "opctexto", e.target.value)}
                            disabled={isReadOnly}
                          />
                        </Grid>
                        <Grid item xs={9} sm={4}>
                          <TextField
                            fullWidth
                            label="Valor/Puntaje"
                            size="small"
                            type="number"
                            value={opc.opcpuntaje}
                            onChange={(e) =>
                              handleChangeOpcion(preg.id, opc.id, "opcpuntaje", parseFloat(e.target.value) || 0)
                            }
                            inputProps={{ step: "0.1" }}
                            disabled={isReadOnly}
                          />
                        </Grid>
                        {!isReadOnly && (
                          <Grid item xs={3} sm={1}>
                            <IconButton color="error" size="small" onClick={() => handleRemoveOpcion(preg.id, opc.id)}>
                              <Delete fontSize="small" />
                            </IconButton>
                          </Grid>
                        )}
                      </Grid>
                    ))}
                    {!isReadOnly && (
                      <Button
                        startIcon={<Add />}
                        size="small"
                        color="primary"
                        onClick={() => handleAddOpcion(preg.id)}
                        sx={{ mt: 1 }}
                      >
                        Añadir Opción
                      </Button>
                    )}
                  </Box>
                )}
              </CardContent>
            </Card>
          ))}

          {!isReadOnly && (
            <Button
              variant="contained"
              color="secondary"
              startIcon={<Add />}
              onClick={handleAddPregunta}
              sx={{ mb: 4, py: 1 }}
            >
              Agregar Nueva Pregunta
            </Button>
          )}
        </Box>
      </div>
    </ThemeProvider>
  )
}

export default EditarEncuesta
